/* ==========================================================================
   jackpot-admin.js
   Four Loko JACKPOT — entrant admin

   SECURITY — read this before changing anything
   ---------------------------------------------
   A Shopify page template is public. Entrant records contain name, date of
   birth, email and phone, so this page must never render them to an
   unauthenticated visitor.

   Two rules follow from that:

   1. Nothing is fetched until Supabase Auth reports a signed-in user. The
      login gate below is a convenience, NOT the security boundary — anyone
      can edit the DOM.

   2. The security boundary is the `jackpot-entries` edge function. It must
      verify the caller's JWT and confirm that user is an admin before it
      returns a single row. Do not expose the entries table to the anon key
      via RLS, or this page's key (which is public in the page source) would
      be enough to dump every entrant.

   Expected contract:

     POST {supabaseUrl}/functions/v1/jackpot-entries
     Headers: Authorization: Bearer <user access token>
              apikey: <anon key>
     Body:    { page, pageSize, search, outcome, flavour, from, to,
                sort: {field, dir}, exportAll }
     200:     { success: true,
                total, page, pageSize,
                stats: { entries, entrants, wins, byPrize: {prize: count} },
                entries: [ { id, created_at, name, dob, age, email, phone,
                             marketing, outcome, flavour, prize } ] }
     403:     { success: false, error: 'Not authorised' }

   Demo mode (a section setting) fills the table with generated rows so the
   layout can be signed off before the backend exists. It must be OFF for live.
   ========================================================================== */

(function () {
  'use strict';

  var PAGE_SIZE = 50;

  document.addEventListener('DOMContentLoaded', function () {
    var root = document.getElementById('jpa');
    if (!root) return;
    new Admin(root).init();
  });

  function Admin(root) {
    this.root = root;
    this.cfg  = JSON.parse(root.getAttribute('data-config') || '{}');
    this.state = {
      page: 1, search: '', outcome: '', flavour: '', from: '', to: '', shipped: '', address: '',
      sort: { field: 'created_at', dir: 'desc' },
      rows: [], total: 0, stats: null
    };
    this.client = null;

    this.el = {
      login:    root.querySelector('#jpa-login'),
      loginForm:root.querySelector('#jpa-login-form'),
      email:    root.querySelector('#jpa-email'),
      password: root.querySelector('#jpa-password'),
      loginMsg: root.querySelector('#jpa-login-msg'),
      loginBtn: root.querySelector('#jpa-login-btn'),
      app:      root.querySelector('#jpa-app'),
      whoami:   root.querySelector('#jpa-whoami'),
      signout:  root.querySelector('#jpa-signout'),
      stats:    root.querySelector('#jpa-stats'),
      search:   root.querySelector('#jpa-search'),
      outcome:  root.querySelector('#jpa-filter-outcome'),
      flavour:  root.querySelector('#jpa-filter-flavour'),
      from:     root.querySelector('#jpa-from'),
      to:       root.querySelector('#jpa-to'),
      clear:    root.querySelector('#jpa-clear'),
      exportBtn:root.querySelector('#jpa-export'),
      refresh:  root.querySelector('#jpa-refresh'),
      testEmail:root.querySelector('#jpa-test-email'),
      count:    root.querySelector('#jpa-count'),
      tablewrap:root.querySelector('.jpa-tablewrap'),
      anBody:   root.querySelector('#jpa-an-body'),
      anToggle: root.querySelector('#jpa-an-toggle'),
      thead:    root.querySelector('#jpa-thead'),
      tbody:    root.querySelector('#jpa-tbody'),
      pager:    root.querySelector('#jpa-pager'),
      prev:     root.querySelector('#jpa-prev'),
      next:     root.querySelector('#jpa-next'),
      pageInfo: root.querySelector('#jpa-pageinfo')
    };
  }

  Admin.prototype.init = function () {
    var self = this;
    this.bind();

    if (this.cfg.demoMode) {
      /* No backend yet — show the layout with generated rows. */
      this.showApp({ email: 'demo@four-loko.test' });
      this.load();
      return;
    }

    if (!this.cfg.supabaseUrl || !this.cfg.supabaseAnonKey) {
      this.loginError('Supabase credentials are missing from theme settings.');
      return;
    }

    waitFor(function () { return window.supabase && window.supabase.createClient; }, function () {
      self.client = window.supabase.createClient(self.cfg.supabaseUrl, self.cfg.supabaseAnonKey);

      /* Resume an existing session so a refresh does not force a re-login */
      self.client.auth.getSession().then(function (res) {
        var session = res && res.data && res.data.session;
        if (session && session.user) {
          self.showApp(session.user);
          self.load();
        }
      });
    });
  };

  Admin.prototype.bind = function () {
    var self = this;

    this.el.loginForm.addEventListener('submit', function (e) {
      e.preventDefault();
      self.signIn();
    });

    this.el.signout.addEventListener('click', function () { self.signOut(); });
    this.el.refresh.addEventListener('click', function () { self.load(); });

    if (this.el.testEmail) {
      this.el.testEmail.addEventListener('click', function () { self.sendTestEmail(); });
    }
    this.el.exportBtn.addEventListener('click', function () { self.exportCsv(); });

    /* Search is debounced — every keystroke would otherwise hit the function */
    var timer;
    this.el.search.addEventListener('input', function () {
      clearTimeout(timer);
      timer = setTimeout(function () {
        self.state.search = self.el.search.value.trim();
        self.state.page = 1;
        self.load();
      }, 300);
    });

    [this.el.outcome, this.el.flavour, this.el.from, this.el.to].forEach(function (input) {
      input.addEventListener('change', function () {
        /* "win:no" and "win:yes" are one control over two fields — the
           fulfilment queue is the view people actually want, and a second
           select for it would be a filter nobody sets. */
        var parts = self.el.outcome.value.split(':');
        self.state.outcome = parts[0] || '';
        self.state.shipped = (parts[1] === 'yes' || parts[1] === 'no') ? parts[1] : '';
        self.state.address = (parts[1] === 'noaddr') ? 'no' : '';
        self.state.flavour = self.el.flavour.value;
        self.state.from    = self.el.from.value;
        self.state.to      = self.el.to.value;

        /* A backwards range returns nothing and looks like a bug, so swap it */
        if (self.state.from && self.state.to && self.state.from > self.state.to) {
          var swap = self.state.from;
          self.state.from = self.state.to;
          self.state.to = swap;
          self.el.from.value = self.state.from;
          self.el.to.value = self.state.to;
        }

        self.state.page = 1;
        self.load();
      });
    });

    this.el.clear.addEventListener('click', function () {
      var s = self.state;
      s.search = s.outcome = s.flavour = s.from = s.to = s.shipped = s.address = '';
      s.page = 1;
      self.el.search.value = self.el.outcome.value = self.el.flavour.value = '';
      self.el.from.value = self.el.to.value = '';
      self.load();
    });

    /* Delegated: the tbody is rewritten on every load, so per-button
       listeners would be lost the moment anything is filtered or sorted. */
    this.el.tbody.addEventListener('click', function (e) {
      var btn = e.target.closest('.jpa-ship');
      if (!btn) return;
      self.setShipped(btn);
    });

    if (this.el.anToggle) {
      this.el.anToggle.addEventListener('click', function () {
        var open = self.el.anToggle.getAttribute('aria-expanded') === 'true';
        self.el.anToggle.setAttribute('aria-expanded', open ? 'false' : 'true');
        self.el.anToggle.textContent = open ? 'Show' : 'Hide';
        self.el.anBody.hidden = open;
      });
    }

    this.el.thead.addEventListener('click', function (e) {
      var th = e.target.closest('th[data-field]');
      if (!th) return;
      var field = th.getAttribute('data-field');
      var s = self.state.sort;
      s.dir = (s.field === field && s.dir === 'desc') ? 'asc' : 'desc';
      s.field = field;
      self.state.page = 1;
      self.load();
    });

    this.el.prev.addEventListener('click', function () {
      if (self.state.page > 1) { self.state.page--; self.load({ toTop: true }); }
    });
    this.el.next.addEventListener('click', function () {
      if (self.state.page * PAGE_SIZE < self.state.total) { self.state.page++; self.load({ toTop: true }); }
    });
  };

  /* ── Auth ─────────────────────────────────────────────────── */

  Admin.prototype.signIn = function () {
    var self = this;
    var email = this.el.email.value.trim();
    var password = this.el.password.value;

    if (!email || !password) return this.loginError('Enter your email and password.');
    if (!this.client) return this.loginError('Still connecting — try again in a moment.');

    this.setLoginBusy(true);
    this.client.auth.signInWithPassword({ email: email, password: password })
      .then(function (res) {
        self.setLoginBusy(false);
        if (res.error) return self.loginError(res.error.message || 'Could not sign in.');
        self.showApp(res.data.user);
        self.load();
      })
      .catch(function () {
        self.setLoginBusy(false);
        self.loginError('Could not sign in. Check your connection and try again.');
      });
  };

  Admin.prototype.signOut = function () {
    var self = this;
    var done = function () {
      self.el.app.hidden = true;
      self.el.login.hidden = false;
      self.state.rows = [];
      self.el.tbody.innerHTML = '';
    };
    if (this.client) this.client.auth.signOut().then(done, done);
    else done();
  };

  Admin.prototype.showApp = function (user) {
    this.el.login.hidden = true;
    this.el.app.hidden = false;
    this.el.whoami.innerHTML = 'Signed in as <strong>' + esc(user && user.email || '') + '</strong>';
  };

  Admin.prototype.loginError = function (msg) {
    this.el.loginMsg.textContent = msg;
    this.el.loginMsg.className = 'jpa-msg';
    this.el.loginMsg.hidden = false;
  };

  Admin.prototype.setLoginBusy = function (busy) {
    this.el.loginBtn.disabled = busy;
    this.el.loginBtn.innerHTML = busy
      ? '<span class="jpa-spinner" aria-hidden="true"></span> Signing in…'
      : 'Sign in';
  };

  /* ── Data ─────────────────────────────────────────────────── */

  Admin.prototype.query = function (extra) {
    var s = this.state;
    var body = {
      page: s.page, pageSize: PAGE_SIZE,
      search: s.search, outcome: s.outcome, flavour: s.flavour,
      from: s.from, to: s.to, shipped: s.shipped, address: s.address,
      sort: s.sort
    };
    for (var k in (extra || {})) body[k] = extra[k];
    return body;
  };

  Admin.prototype.fetchEntries = function (extra) {
    if (this.cfg.demoMode) return demoEntries(this.cfg, this.query(extra));
    return this.request(this.query(extra));
  };

  /* Every call to the edge function goes through here — the session token is
     fetched fresh each time rather than cached, so a long shift at the screen
     does not end in silent 401s once the access token has rotated. */
  Admin.prototype.request = function (body) {
    if (this.cfg.demoMode) return demoRequest(body);

    var self = this;
    return this.client.auth.getSession().then(function (res) {
      var session = res && res.data && res.data.session;
      if (!session) throw new Error('Session expired — please sign in again.');

      return fetch(self.cfg.supabaseUrl.replace(/\/$/, '') + '/functions/v1/jackpot-entries', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + session.access_token,
          'apikey': self.cfg.supabaseAnonKey
        },
        body: JSON.stringify(body)
      }).then(function (r) { return r.json(); });
    });
  };

  /* opts.keepPosition — stay exactly where the page is (used after a toggle)
     opts.toTop        — bring the top of the table into view (used by paging)

     Reloading used to replace the whole tbody with a single "Loading…" row.
     On a 50-row table that collapses roughly 2,500px of page in one frame and
     restores it a moment later, which is what made rows appear to jump: the
     browser keeps the scroll offset, so the content underneath it moves. The
     existing rows now stay on screen, dimmed, and the container holds its
     height until the new ones are in. */
  Admin.prototype.load = function (opts) {
    opts = opts || {};
    var self = this;
    var wrap = this.el.tablewrap;
    var hasRows = this.state.rows.length > 0;

    if (hasRows) {
      /* Pin the height so nothing reflows while the request is out */
      wrap.style.minHeight = wrap.getBoundingClientRect().height + 'px';
      this.root.classList.add('jpa-busy');
    } else {
      this.el.tbody.innerHTML =
        '<tr><td colspan="10" class="jpa-empty"><span class="jpa-spinner"></span> Loading…</td></tr>';
    }

    var settle = function () {
      self.root.classList.remove('jpa-busy');
      /* Release the pinned height only after the new rows have been laid out,
         or the container snaps to the old size for a frame first */
      requestAnimationFrame(function () { wrap.style.minHeight = ''; });
    };

    this.fetchEntries().then(function (res) {
      if (!res || res.success === false) {
        var msg = (res && res.error) || 'Could not load entries.';
        self.state.rows = [];
        self.el.tbody.innerHTML = '<tr><td colspan="10" class="jpa-empty">' + esc(msg) + '</td></tr>';
        settle();
        return;
      }
      self.state.rows  = res.entries || [];
      self.state.total = res.total || 0;
      self.state.stats = res.stats || null;
      self.render();
      settle();

      if (opts.toTop) self.scrollToTable();
      if (!opts.keepPosition) self.loadAnalytics();
    }).catch(function (err) {
      self.state.rows = [];
      self.el.tbody.innerHTML =
        '<tr><td colspan="10" class="jpa-empty">' + esc(err.message || 'Could not load entries.') + '</td></tr>';
      settle();
    });
  };

  /* Paging keeps the scroll offset by default, which lands you in the middle
     of the next page's rows. Go to the top of the table instead — the same
     place every time, so the list does not appear to have moved. */
  Admin.prototype.scrollToTable = function () {
    var wrap = this.el.tablewrap;
    if (!wrap) return;
    var top = wrap.getBoundingClientRect().top + window.pageYOffset - 16;
    window.scrollTo({
      top: Math.max(0, top),
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
    });
  };

  /* Preview the winner email without having to win one. Demo mode cannot do
     this — there is no signed-in user to send to, and a public send endpoint
     would be an open relay — so it needs demo mode off and a real login. */
  Admin.prototype.sendTestEmail = function () {
    var btn = this.el.testEmail;
    if (!btn) return;

    if (this.cfg.demoMode) {
      alert('Turn demo mode off and sign in first.\n\nThe test email is sent to the address you are signed in with, which demo mode does not have.');
      return;
    }

    btn.disabled = true;
    var previous = btn.textContent;
    btn.textContent = 'Sending…';

    var done = function (message) {
      btn.disabled = false;
      btn.textContent = previous;
      alert(message);
    };

    this.request({ action: 'testEmail' })
      .then(function (res) {
        if (res && res.success) {
          done('Test email sent to ' + (res.sentTo || 'your address') +
               '.\n\nIf it does not arrive, check your spam folder — a brand new sending domain often lands there for the first few messages.');
        } else {
          done('Could not send it.\n\n' + ((res && res.error) || 'No reason given.'));
        }
      })
      .catch(function (err) {
        done('Could not send it.\n\n' + (err.message || 'Check your connection and try again.'));
      });
  };

  /* ── Analytics ────────────────────────────────────────────── */

  Admin.prototype.loadAnalytics = function () {
    var self = this;
    var body = this.el.anBody;
    if (!body) return;

    if (!body.innerHTML) {
      body.innerHTML = '<p class="jpa-empty"><span class="jpa-spinner"></span> Loading analytics…</p>';
    }

    var req = this.cfg.demoMode
      ? demoAnalytics(this.state)
      : this.request({ action: 'analytics', from: this.state.from, to: this.state.to });

    req.then(function (res) {
      if (!res || res.success === false || !res.analytics) {
        body.innerHTML = '<p class="jpa-empty">' +
          esc((res && res.error) || 'Could not load analytics.') + '</p>';
        return;
      }
      body.innerHTML = renderAnalytics(res.analytics, self.cfg.demoMode);
    }).catch(function () {
      body.innerHTML = '<p class="jpa-empty">Could not load analytics.</p>';
    });
  };

  /* ── Fulfilment ───────────────────────────────────────────── */

  /* Marking a prize sent. The button is disabled for the round trip rather
     than updated optimistically: this is a record of something that happened
     in the real world, so it should not appear done until the server says so. */
  Admin.prototype.setShipped = function (btn) {
    var self = this;
    var id      = btn.getAttribute('data-id');
    var shipped = btn.getAttribute('data-shipped') !== '1';   // toggle

    /* The badge sits in a dense table and is small, so a misclick is easy and
       the consequence is real: a prize marked sent drops out of the "to send"
       queue and may never be posted. Confirm names the person and the prize
       so it can be checked rather than dismissed reflexively. */
    var row  = this.state.rows.filter(function (r) { return r.id === id; })[0] || {};
    var who  = row.name ? row.name + ' — ' + (row.prize || 'prize') : 'this entry';
    var ask  = shipped
      ? 'Mark this prize as SENT?\n\n' + who + '\n\nIt will drop out of the "to send" list.'
      : 'Undo "sent" for this prize?\n\n' + who + '\n\nIt will go back into the "to send" list.';

    if (!window.confirm(ask)) return;

    btn.disabled = true;
    var previous = btn.textContent;
    btn.textContent = '…';

    this.request({ action: 'setShipped', id: id, shipped: shipped })
      .then(function (res) {
        if (!res || res.success === false) {
          btn.disabled = false;
          btn.textContent = previous;
          alert((res && res.error) || 'Could not update that entry.');
          return;
        }

        /* Update the row in place, then refresh so the counters and any
           "to send" filter follow. */
        var row = self.state.rows.filter(function (r) { return r.id === id; })[0];
        if (row) {
          row.shipped_at = res.shipped_at;
          row.shipped_by = res.shipped_by;
        }
        self.load({ keepPosition: true });
      })
      .catch(function () {
        btn.disabled = false;
        btn.textContent = previous;
        alert('Could not update that entry. Check your connection and try again.');
      });
  };

  /* ── Render ───────────────────────────────────────────────── */

  Admin.prototype.render = function () {
    var s = this.state;

    /* stats */
    if (s.stats) {
      this.el.stats.innerHTML =
        stat('Entries', number(s.stats.entries)) +
        stat('Unique entrants', number(s.stats.entrants)) +
        stat('Instant wins', number(s.stats.wins), 'jpa-stat--wins') +
        stat('Win rate', s.stats.entries ? ((s.stats.wins / s.stats.entries) * 100).toFixed(1) + '%' : '—') +
        stat('Prizes sent', number(s.stats.shipped || 0), 'jpa-stat--sent') +
        stat('Still to send', number(s.stats.toSend || 0), 'jpa-stat--tosend') +
        stat('Awaiting address', number(s.stats.noAddress || 0), 'jpa-stat--noaddr') +
        (s.stats.emailFailed
          ? stat('Email failed', number(s.stats.emailFailed), 'jpa-stat--noaddr')
          : '');
    }

    /* sort indicators */
    Array.prototype.forEach.call(this.el.thead.querySelectorAll('th[data-field]'), function (th) {
      var f = th.getAttribute('data-field');
      if (f === s.sort.field) th.setAttribute('aria-sort', s.sort.dir === 'asc' ? 'ascending' : 'descending');
      else th.removeAttribute('aria-sort');
    });

    /* rows */
    if (!s.rows.length) {
      this.el.tbody.innerHTML = '<tr><td colspan="10" class="jpa-empty">No entries match those filters.</td></tr>';
    } else {
      this.el.tbody.innerHTML = s.rows.map(function (r) {
        var won = r.outcome === 'win';

        /* One line per cell. Everything that was stacked is either its own
           column now or sits inline after the value it belongs to. */
        var sent = won && !!r.shipped_at;

        var noAddr = won && !r.address_at;

        return '<tr class="' + (won ? (sent ? 'jpa-row--sent' : 'jpa-row--win') : '') +
                 (noAddr ? ' jpa-row--noaddr' : '') + '">' +
          td(formatDate(r.created_at), 'jpa-when', 'Entered') +
          /* The reference is on the name, not a column of its own — it is
             looked up by search far more often than it is read off screen. */
          td('<span class="jpa-person__name"' +
               (r.reference ? ' title="Ref ' + esc(r.reference) + '"' : '') + '>' +
               esc(r.name) + '</span>', 'jpa-nowrap', 'Name') +
          td('<a class="jpa-person__link" href="mailto:' + esc(r.email) + '">' + esc(r.email) + '</a>', 'jpa-nowrap', 'Email') +
          td(r.phone
              ? '<a class="jpa-person__link" href="tel:' + esc(r.phone) + '">' + esc(r.phone) + '</a>'
              : '<span class="jpa-muted">&ndash;</span>', 'jpa-nowrap', 'Mobile') +
          td(r.age != null
              ? formatDob(r.dob) + ' <span class="jpa-age">' + r.age + '</span>'
              : '<span class="jpa-muted">&ndash;</span>', 'jpa-nowrap', 'DOB & age') +
          td(won ? shipButton(r) : '<span class="jpa-muted">No win</span>', '', 'Result') +
          td(r.prize ? esc(r.prize) : '<span class="jpa-muted">&ndash;</span>', 'jpa-nowrap', 'Prize') +
          td(r.flavour ? '<span class="jpa-flavour">' + esc(r.flavour) + '</span>'
                       : '<span class="jpa-muted">&ndash;</span>', 'jpa-nowrap', 'Flavour') +
          td(addressCell(r), 'jpa-nowrap jpa-addr', 'Delivery address') +
          td(r.marketing
              ? '<span class="jpa-tag jpa-tag--yes">Opted in</span>'
              : '<span class="jpa-muted">&ndash;</span>', '', 'Marketing') +
        '</tr>';
      }).join('');
    }

    /* "Clear filters" is only meaningful when something is filtered */
    this.el.clear.hidden = !(s.search || s.outcome || s.flavour || s.from || s.to || s.shipped || s.address);

    /* count + pager */
    var from = s.total ? ((s.page - 1) * PAGE_SIZE) + 1 : 0;
    var to   = Math.min(s.page * PAGE_SIZE, s.total);
    this.el.count.textContent = s.total
      ? 'Showing ' + number(from) + '–' + number(to) + ' of ' + number(s.total)
      : 'No entries';

    var pages = Math.max(1, Math.ceil(s.total / PAGE_SIZE));
    this.el.pageInfo.textContent = 'Page ' + s.page + ' of ' + pages;
    this.el.prev.disabled = s.page <= 1;
    this.el.next.disabled = s.page >= pages;
    this.el.pager.hidden = s.total <= PAGE_SIZE;
  };

  /* ── CSV ──────────────────────────────────────────────────── */

  Admin.prototype.exportCsv = function () {
    var self = this;
    var btn = this.el.exportBtn;
    btn.disabled = true;
    btn.textContent = 'Preparing…';

    /* exportAll asks the function for every matching row, not just this page */
    this.fetchEntries({ exportAll: true, page: 1 }).then(function (res) {
      var rows = (res && res.entries) || [];
      var cols = ['reference','created_at','name','dob','age','email','phone','outcome','flavour','prize','marketing',
                  'address_line1','address_line2','address_city','address_postcode','address_country','address_at','email_sent_at','email_error',
                  'shipped_at','shipped_by'];
      var csv  = [cols.join(',')].concat(rows.map(function (r) {
        return cols.map(function (c) { return csvCell(r[c]); }).join(',');
      })).join('\r\n');

      /* BOM so Excel opens UTF-8 correctly */
      var blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
      var url  = URL.createObjectURL(blob);
      var a    = document.createElement('a');
      a.href = url;
      a.download = 'four-loko-jackpot-entries.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    }).catch(function () {
      alert('Could not build the export. Please try again.');
    }).then(function () {
      btn.disabled = false;
      btn.textContent = 'Export CSV';
    });
  };

  /* ── Demo data ────────────────────────────────────────────── */

  /* Demo mode has no backend, so shipped state lives here for the session.
     It resets on reload, which is the honest behaviour — nothing was saved. */
  var DEMO_SHIPPED = {};

  function demoRequest(body) {
    if (body.action === 'setShipped') {
      if (body.shipped) DEMO_SHIPPED[body.id] = new Date().toISOString();
      else delete DEMO_SHIPPED[body.id];
      return new Promise(function (resolve) {
        setTimeout(function () {
          resolve({ success: true, id: body.id,
                    shipped_at: DEMO_SHIPPED[body.id] || null,
                    shipped_by: DEMO_SHIPPED[body.id] ? 'demo@four-loko.test' : null });
        }, 200);
      });
    }
    return demoEntries({}, body);
  }

  function demoEntries(cfg, q) {
    var FIRST = ['Alex','Sam','Jess','Chloe','Ben','Priya','Liam','Aisha','Tom','Grace','Ruby','Josh','Maya','Kai','Erin'];
    var LAST  = ['Baker','Ncube','Patel','Doyle','Hughes','Kowalski','Reid','Okafor','Byrne','Sharma','Wallace','Green'];
    var flavours = (cfg && cfg.flavours && cfg.flavours.length) ? cfg.flavours : [
      { key: 'jackpot', name: 'Jackpot', prize: 'Four Loko T-Shirt' }
    ];

    var all = [];
    for (var i = 0; i < 137; i++) {
      var f = flavours[i % flavours.length];
      var win = i % 7 === 0;
      var first = FIRST[i % FIRST.length], last = LAST[(i * 3) % LAST.length];
      var year = 1990 + (i % 15);
      all.push({
        id: 'demo-' + i,
        reference: ('DEMO' + String(1000 + i)).toUpperCase(),
        created_at: new Date((cfg && cfg.demoNow ? cfg.demoNow : Date.now()) - i * 3600000 * 5).toISOString(),
        name: first + ' ' + last,
        dob: year + '-0' + ((i % 9) + 1) + '-1' + (i % 9),
        age: 2026 - year,
        email: (first + '.' + last).toLowerCase() + '@example.com',
        phone: '0770090' + String(1000 + i).slice(-4),
        outcome: win ? 'win' : 'lose',
        flavour: win ? f.name : null,
        prize: win ? f.prize : null,
        marketing: i % 3 !== 0,
        address_line1: win ? (12 + (i % 80)) + ' Example Street' : null,
        address_line2: null,
        address_city: win ? ['Manchester','Leeds','Bristol','Glasgow','Cardiff'][i % 5] : null,
        address_postcode: win ? ['M1 4AB','LS2 7EX','BS1 5TF','G2 8DL','CF10 1EP'][i % 5] : null,
        address_country: win ? 'United Kingdom' : null,
        address_at: (win && i % 9 !== 0) ? new Date().toISOString() : null,
        email_sent_at: (win && i % 9 !== 0 && i % 21 !== 0) ? new Date().toISOString() : null,
        email_error: (win && i % 9 !== 0 && i % 21 === 0) ? 'Resend 422: invalid recipient' : null,
        shipped_at: DEMO_SHIPPED['demo-' + i] || null,
        shipped_by: DEMO_SHIPPED['demo-' + i] ? 'demo@four-loko.test' : null
      });
    }

    /* mirror what the edge function is expected to do server-side */
    var rows = all.filter(function (r) {
      if (q.outcome && r.outcome !== q.outcome) return false;
      if (q.flavour && r.flavour !== q.flavour) return false;
      if (q.address === 'no'  &&  r.address_at) return false;
      if (q.address === 'yes' && !r.address_at) return false;
      if (q.shipped === 'yes' && !r.shipped_at) return false;
      if (q.shipped === 'no'  &&  r.shipped_at) return false;
      if (q.from && r.created_at < q.from) return false;
      if (q.to   && r.created_at > q.to + 'T23:59:59.999Z') return false;
      if (q.search) {
        var hay = (r.name + ' ' + r.email + ' ' + r.phone + ' ' + (r.reference || '')).toLowerCase();
        if (hay.indexOf(q.search.toLowerCase()) === -1) return false;
      }
      return true;
    });

    var dir = q.sort.dir === 'asc' ? 1 : -1;
    rows.sort(function (a, b) {
      var x = a[q.sort.field], y = b[q.sort.field];
      if (x == null) return 1;
      if (y == null) return -1;
      return x > y ? dir : x < y ? -dir : 0;
    });

    /* Over the filtered set, matching what jackpot_admin_stats does server-side.
       Computing these over every row made the headline numbers contradict the
       table under them the moment a filter was applied. */
    var emails = {};
    rows.forEach(function (r) { emails[r.email] = 1; });
    var stats = {
      entries: rows.length,
      entrants: Object.keys(emails).length,
      wins: rows.filter(function (r) { return r.outcome === 'win'; }).length,
      shipped: rows.filter(function (r) { return r.outcome === 'win' && r.shipped_at; }).length,
      toSend:  rows.filter(function (r) { return r.outcome === 'win' && !r.shipped_at; }).length,
      noAddress: rows.filter(function (r) { return r.outcome === 'win' && !r.address_at; }).length,
      emailFailed: rows.filter(function (r) { return r.outcome === 'win' && r.address_at && !r.email_sent_at; }).length,
      byPrize: {}
    };

    var page = q.exportAll ? rows : rows.slice((q.page - 1) * q.pageSize, q.page * q.pageSize);

    return new Promise(function (resolve) {
      setTimeout(function () {
        resolve({ success: true, total: rows.length, page: q.page, pageSize: q.pageSize,
                  stats: stats, entries: page, demo: true });
      }, 250);
    });
  }

  /* ── Helpers ──────────────────────────────────────────────── */

  function stat(label, value, cls) {
    return '<div class="jpa-stat ' + (cls || '') + '">' +
             '<p class="jpa-stat__label">' + esc(label) + '</p>' +
             '<p class="jpa-stat__value">' + esc(value) + '</p>' +
           '</div>';
  }

  /* data-label is what the mobile card layout shows in place of the column
     header, which is hidden below 700px — see jackpot-admin.css. */
  /* The win badge is the control. A separate "mark sent" button would be a
     tenth column carrying one word, and the badge is already the thing the
     eye goes to on a winning row. */
  /* One line, because the table is one line per entry. The full address is on
     the title attribute and in the CSV — this is for spotting who is missing
     one, not for reading addresses off the screen. */
  function addressCell(r) {
    if (r.outcome !== 'win') return '<span class="jpa-muted">&ndash;</span>';

    if (!r.address_at) {
      return '<span class="jpa-tag jpa-tag--missing" title="This winner has not given a delivery address yet">No address</span>';
    }

    var parts = [r.address_line1, r.address_line2, r.address_city,
                 r.address_postcode, r.address_country]
      .filter(Boolean).join(', ');

    var mail = r.email_sent_at
      ? ''
      : '<span class="jpa-tag jpa-tag--missing" title="' +
          esc(r.email_error || 'The confirmation email has not gone yet') +
        '" style="margin-left:6px">No email</span>';

    return '<span class="jpa-addr__text" title="' + esc(parts) + '">' + esc(parts) + '</span>' + mail;
  }

  function shipButton(r) {
    var sent = !!r.shipped_at;
    var when = sent ? formatDatePlain(r.shipped_at) : '';
    var title = sent
      ? 'Sent ' + when + (r.shipped_by ? ' by ' + r.shipped_by : '') + ' — click to undo'
      : 'Click to mark this prize as sent';

    return '<button type="button" class="jpa-tag jpa-ship ' +
             (sent ? 'jpa-tag--sent' : 'jpa-tag--win') + '"' +
           ' data-id="' + esc(r.id) + '"' +
           ' data-shipped="' + (sent ? '1' : '0') + '"' +
           ' aria-pressed="' + (sent ? 'true' : 'false') + '"' +
           ' title="' + esc(title) + '">' +
           (sent ? 'Sent' : 'Win') +
           '</button>';
  }

  function formatDatePlain(iso) {
    var d = new Date(iso);
    if (isNaN(d)) return '';
    return pad(d.getDate()) + ' ' + MONTHS[d.getMonth()] + ' ' + d.getFullYear();
  }

  function td(html, cls, label) {
    return '<td' + (cls ? ' class="' + cls + '"' : '') +
           (label ? ' data-label="' + label + '"' : '') + '>' + html + '</td>';
  }

  var MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  /* "14 Aug 2026 / 16:34" — the year is repeated on every row of a six-week
     campaign, but it is part of the record, so it stays and is simply set
     smaller and quieter than the day it belongs to. */
  function formatDate(iso) {
    if (!iso) return '—';
    var d = new Date(iso);
    if (isNaN(d)) return esc(iso);
    return '<span class="jpa-when__day">' + pad(d.getDate()) + ' ' + MONTHS[d.getMonth()] +
             '<span class="jpa-when__year"> ' + String(d.getFullYear()).slice(2) + '</span></span>' +
           '<span class="jpa-when__time">' + pad(d.getHours()) + ':' + pad(d.getMinutes()) + '</span>';
  }

  function formatDob(v) {
    if (!v) return '—';
    var p = String(v).split('-');
    return p.length === 3 ? p[2] + '/' + p[1] + '/' + p[0] : esc(v);
  }

  function csvCell(v) {
    if (v == null) return '';
    var s = String(v);
    /* prefix formula triggers so a spreadsheet cannot execute injected content */
    if (/^[=+\-@\t\r]/.test(s)) s = "'" + s;
    return '"' + s.replace(/"/g, '""') + '"';
  }

  function number(n) { return (n == null ? 0 : n).toLocaleString('en-GB'); }
  function pad(n) { return n < 10 ? '0' + n : '' + n; }

  function esc(v) {
    return String(v == null ? '' : v)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }


  /* ==========================================================
     ANALYTICS RENDERING
     Charts are CSS bars, not a charting library — the page loads no
     third-party JavaScript, and for a funnel, a daily count and an hour
     histogram a div with a width is genuinely enough.
     ========================================================== */

  /* Page views did not reach the events table before this date. page_view was
     the only event sent with navigator.sendBeacon, and a beacon carrying an
     application/json Blob needs a CORS preflight that Safari and the in-app
     browsers QR traffic arrives in silently drop — see the note in
     jackpot.js. Fixed 2 Sept 2026; 1 Sept and the first part of 2 Sept have
     no view data and never will.

     A window reaching back past this shows "not recorded" rather than 0. A
     zero here reads as "nobody came", which is not merely wrong but wrong in
     a direction somebody might act on — and it is the number a conversion
     rate would then be divided by. */
  var VIEWS_FROM       = '2026-09-02';
  var VIEWS_FROM_LABEL = '2 September';

  /* Where from, Location, Devices and the visits half of Day by day are all
     built from page_view rows too, so they are missing exactly the days the
     funnel's top row is. Each carries the caveat itself rather than the
     section carrying it once: these panels get read — and screenshotted —
     one at a time, and a note at the top would not travel with them. */
  function blindNote(blind) {
    return blind
      ? '<p class="jpa-an__foot">Excludes visits before ' + VIEWS_FROM_LABEL +
        ', which were not recorded.</p>'
      : '';
  }

  function blindEmpty(blind, normal) {
    return blind ? 'Not recorded before ' + VIEWS_FROM_LABEL + '.' : normal;
  }

  function renderAnalytics(a, demo) {
    /* Any day on screen that predates the fix has no page_view data behind it.
       Demo figures are generated across a rolling 28 days that reach back past
       it, and they are complete by construction — exempt them, or every client
       walkthrough claims the views are missing. */
    var blind = !demo &&
      (a.daily || []).some(function (d) { return String(d.day) < VIEWS_FROM; });

    return '<div class="jpa-an__grid">' +
             panelFunnel(a.funnel || {}, blind) +
             panelPrizes(a.prizes || {}) +
           '</div>' +
           panelDaily(a.daily || [], blind) +
           '<div class="jpa-an__grid jpa-an__grid--3">' +
             panelTable('Where from', ['Source', 'Sessions'], (a.sources || []).map(function (r) {
               return [r.source + (r.medium ? ' <span class="jpa-muted">/ ' + esc(r.medium) + '</span>' : ''),
                       number(r.sessions)];
             }), 'No traffic recorded yet.', blind) +
             panelTable('Location', ['Place', 'Sessions'], (a.places || []).map(function (r) {
               return [r.place, number(r.sessions)];
             }), 'No location data yet.', blind) +
             panelDevices(a.devices || [], blind) +
           '</div>' +
           panelHours(a.hours || []);
  }

  /* The headline. Each step as a share of the one above it, because "how many
     of the people who saw it went on to enter" is the only question this
     page is really being asked. */
  function panelFunnel(f, blind) {
    var steps = [
      { label: 'Saw the page',   value: f.views || 0 },
      { label: 'Opened the form', value: f.formOpens || 0 },
      { label: 'Entered',         value: f.entries || 0 },
      { label: 'Pulled the lever', value: f.spins || 0 }
    ];

    /* Scale against the tallest step we can actually trust, or every bar
       below a missing top row is drawn full width. */
    var top = (blind
      ? Math.max(steps[1].value, steps[2].value, steps[3].value)
      : steps[0].value) || 1;

    var rows = steps.map(function (st, i) {
      var missing = blind && i === 0;
      /* The row under a missing one has nothing to be a share OF */
      var noShare = i === 0 || (blind && i === 1);
      var prev    = i === 0 ? null : steps[i - 1].value;
      var share   = prev ? (st.value / prev) * 100 : 100;

      return '<div class="jpa-fn__row">' +
               '<div class="jpa-fn__label">' + esc(st.label) + '</div>' +
               '<div class="jpa-fn__bar"><span style="width:' +
                 (missing ? '0' : Math.max(1, Math.min(100, (st.value / top) * 100)).toFixed(1)) +
                 '%"></span></div>' +
               '<div class="jpa-fn__value">' +
                 (missing
                   ? '<span class="jpa-muted">not recorded</span>'
                   : number(st.value)) +
                 (noShare ? '' : ' <span class="jpa-muted">' + share.toFixed(0) + '%</span>') +
               '</div>' +
             '</div>';
    }).join('');

    var conv = f.views ? ((f.entries || 0) / f.views) * 100 : 0;

    var lede = blind
      ? 'Page views were not recorded before ' + VIEWS_FROM_LABEL + ', so this ' +
        'window has no visit figure and no conversion rate. Everything below ' +
        '"Saw the page" is complete. Set the date filter to ' + VIEWS_FROM_LABEL +
        ' or later for a full funnel.'
      : 'Unique visits, not page loads. <strong>' + conv.toFixed(1) +
        '%</strong> of visits become an entry.';

    return '<div class="jpa-an__panel">' +
             '<h3 class="jpa-an__h">Funnel</h3>' +
             '<p class="jpa-an__lede">' + lede + '</p>' +
             '<div class="jpa-fn">' + rows + '</div>' +
             (f.viewHits ? '<p class="jpa-an__foot">' + number(f.viewHits) +
               ' page loads from ' + number(f.views || 0) + ' visits.</p>' : '') +
           '</div>';
  }

  /* Renamed from "Prize velocity" — that read as jargon to anyone who had not
     built it. It is a stock list: what has gone, what is left, per prize. */
  function panelPrizes(p) {
    var total     = p.total || 0;
    var claimed   = p.claimed || 0;
    var remaining = p.remaining || 0;
    var pct       = total ? (claimed / total) * 100 : 0;

    /* Per prize, because "how many caps have gone" is the question actually
       asked, and one combined figure for all 400 could not answer it. */
    var rows = (p.byPrize || []).map(function (b) {
      var bt = b.total || 0;
      var bc = b.claimed || 0;
      var bp = bt ? (bc / bt) * 100 : 0;
      return '<div class="jpa-prz">' +
               '<div class="jpa-prz__name">' + esc(b.prize || b.flavour || '') + '</div>' +
               '<div class="jpa-prz__bar"><span style="width:' + bp.toFixed(1) + '%"></span></div>' +
               '<div class="jpa-prz__n">' + number(bc) + '<span class="jpa-muted"> / ' + number(bt) + '</span></div>' +
               '<div class="jpa-prz__left">' + number(b.remaining || 0) + ' left</div>' +
             '</div>';
    }).join('');

    /* Plain English, and phrased as a date rather than a rate — "10.7 a day"
       means nothing without doing the arithmetic yourself. */
    var daily = (p.claimedDaily || []).slice(-7);
    var perDay = daily.length
      ? daily.reduce(function (n, d) { return n + (d.claimed || 0); }, 0) / daily.length
      : 0;

    var outlook;
    if (!total) {
      outlook = 'No prizes have been set up yet.';
    } else if (!remaining) {
      outlook = 'Every prize has been won.';
    } else if (perDay <= 0) {
      outlook = 'No prizes won in the last few days, so there is nothing to forecast from yet.';
    } else {
      var days = Math.ceil(remaining / perDay);
      var when = new Date(Date.now() + days * 86400000);
      var end  = p.lastAt ? new Date(p.lastAt) : null;
      var runsOut = when.getDate() + ' ' + MONTHS[when.getMonth()];

      outlook = 'Prizes are being won at about ' + Math.round(perDay) + ' a day. ' +
                'At that rate the last one goes around <strong>' + runsOut + '</strong>';

      if (end && when > end) {
        outlook += ', which is after the promotion closes — ' +
                   '<strong class="jpa-warn">some prizes look likely to go unclaimed.</strong>';
      } else if (end) {
        outlook += ', before the promotion closes on ' +
                   end.getDate() + ' ' + MONTHS[end.getMonth()] + '.';
      } else {
        outlook += '.';
      }
    }

    return '<div class="jpa-an__panel">' +
             '<h3 class="jpa-an__h">Prizes won</h3>' +
             '<p class="jpa-an__lede"><strong>' + number(claimed) + ' of ' + number(total) +
               '</strong> instant prizes have been won. ' + number(remaining) + ' still available.</p>' +
             '<div class="jpa-meter"><span style="width:' + pct.toFixed(1) + '%"></span></div>' +
             (rows ? '<div class="jpa-przs">' + rows + '</div>' : '') +
             '<p class="jpa-an__foot">' + outlook + '</p>' +
           '</div>';
  }

  function panelDaily(rows, blind) {
    if (!rows.length) {
      return panelWrap('Day by day', '<p class="jpa-empty">Nothing recorded yet.</p>');
    }
    var max = Math.max.apply(null, rows.map(function (r) {
      return Math.max(r.views || 0, r.entries || 0);
    })) || 1;

    var bars = rows.map(function (r) {
      var d = new Date(r.day + 'T12:00:00Z');
      /* Per day, not per chart: a window can straddle the fix, and hovering a
         1 Sept bar must not report "0 visits" while 2 Sept reports a real one. */
      var noViews = blind && String(r.day) < VIEWS_FROM;
      return '<div class="jpa-day" title="' + esc(r.day) + ' — ' +
               (noViews ? 'visits not recorded' : number(r.views || 0) + ' visits') + ', ' +
               number(r.entries || 0) + ' entries">' +
               '<div class="jpa-day__bars">' +
                 '<span class="jpa-day__v" style="height:' + ((r.views || 0) / max * 100).toFixed(1) + '%"></span>' +
                 '<span class="jpa-day__e" style="height:' + ((r.entries || 0) / max * 100).toFixed(1) + '%"></span>' +
               '</div>' +
               '<div class="jpa-day__label">' + d.getDate() + '<span>' + MONTHS[d.getMonth()] + '</span></div>' +
             '</div>';
    }).join('');

    return panelWrap('Day by day',
      '<p class="jpa-an__lede">' +
        '<span class="jpa-swatch jpa-swatch--v"></span>Visits' +
        '<span class="jpa-swatch jpa-swatch--e"></span>Entries</p>' +
      '<div class="jpa-days">' + bars + '</div>' +
      (blind
        ? '<p class="jpa-an__foot">Days before ' + VIEWS_FROM_LABEL +
          ' show no visits bar because none was recorded — their entries are complete.</p>'
        : ''));
  }

  function panelHours(rows) {
    if (!rows.length) return '';
    var by = {};
    rows.forEach(function (r) { by[r.hour] = r.entries || 0; });
    var max = Math.max.apply(null, rows.map(function (r) { return r.entries || 0; })) || 1;

    var bars = '';
    for (var h = 0; h < 24; h++) {
      var v = by[h] || 0;
      bars += '<div class="jpa-hour" title="' + pad(h) + ':00 — ' + number(v) + ' entries">' +
                '<span style="height:' + (v / max * 100).toFixed(1) + '%"></span>' +
                '<em>' + (h % 3 === 0 ? pad(h) : '') + '</em>' +
              '</div>';
    }
    return panelWrap('When people play',
      '<p class="jpa-an__lede">Entries by hour, UK time.</p>' +
      '<div class="jpa-hours">' + bars + '</div>');
  }

  function panelDevices(rows, blind) {
    var total = rows.reduce(function (n, r) { return n + (r.sessions || 0); }, 0);
    if (!total) {
      return panelWrap('Devices',
        '<p class="jpa-empty">' + esc(blindEmpty(blind, 'No device data yet.')) + '</p>', true);
    }

    var items = rows.map(function (r) {
      var pc = (r.sessions / total) * 100;
      return '<div class="jpa-fn__row">' +
               '<div class="jpa-fn__label">' + esc(r.device) + '</div>' +
               '<div class="jpa-fn__bar"><span style="width:' + pc.toFixed(1) + '%"></span></div>' +
               '<div class="jpa-fn__value">' + pc.toFixed(0) + '%</div>' +
             '</div>';
    }).join('');
    return panelWrap('Devices', '<div class="jpa-fn">' + items + '</div>' + blindNote(blind), true);
  }

  function panelTable(title, heads, rows, empty, blind) {
    if (!rows.length) {
      return panelWrap(title,
        '<p class="jpa-empty">' + esc(blindEmpty(blind, empty)) + '</p>', true);
    }
    var body = rows.map(function (r) {
      return '<tr><td>' + r[0] + '</td><td class="jpa-an__num">' + r[1] + '</td></tr>';
    }).join('');
    return panelWrap(title,
      '<table class="jpa-an__table"><thead><tr><th>' + esc(heads[0]) +
      '</th><th class="jpa-an__num">' + esc(heads[1]) + '</th></tr></thead><tbody>' +
      body + '</tbody></table>' + blindNote(blind), true);
  }

  function panelWrap(title, inner, plain) {
    return '<div class="jpa-an__panel' + (plain ? ' jpa-an__panel--plain' : '') + '">' +
             '<h3 class="jpa-an__h">' + esc(title) + '</h3>' + inner +
           '</div>';
  }

  function pair(label, value) {
    return '<div class="jpa-pair"><span>' + esc(label) + '</span><strong>' + value + '</strong></div>';
  }

  /* Demo analytics, so the section can be shown to the client before any real
     traffic exists. Shaped like plausible campaign data, not random noise. */
  function demoAnalytics(state) {
    var days = [];
    var base = Date.now();
    for (var i = 27; i >= 0; i--) {
      var d = new Date(base - i * 86400000);
      var weekend = d.getDay() === 0 || d.getDay() === 6;
      var views = Math.round(180 + (27 - i) * 9 + (weekend ? 120 : 0) + (i % 5) * 14);
      days.push({
        day: d.toISOString().slice(0, 10),
        views: views,
        entries: Math.round(views * 0.34),
        wins: Math.round(views * 0.34 * 0.07)
      });
    }
    var views   = days.reduce(function (n, d) { return n + d.views; }, 0);
    var entries = days.reduce(function (n, d) { return n + d.entries; }, 0);

    return Promise.resolve({ success: true, analytics: {
      funnel: { views: views, viewHits: Math.round(views * 1.4),
                formOpens: Math.round(views * 0.52), formSubmits: entries,
                entries: entries, spins: Math.round(entries * 0.88) },
      daily: days,
      sources: [
        { source: '(direct or QR)', medium: '', sessions: Math.round(views * 0.61) },
        { source: 'instagram.com',  medium: '', sessions: Math.round(views * 0.18) },
        { source: 'l.facebook.com', medium: '', sessions: Math.round(views * 0.09) },
        { source: 'google.com',     medium: '', sessions: Math.round(views * 0.07) },
        { source: 'tiktok.com',     medium: '', sessions: Math.round(views * 0.05) }
      ],
      devices: [
        { device: 'mobile',  sessions: Math.round(views * 0.82) },
        { device: 'desktop', sessions: Math.round(views * 0.13) },
        { device: 'tablet',  sessions: Math.round(views * 0.05) }
      ],
      places: [
        { place: 'Europe/London', sessions: Math.round(views * 0.94) },
        { place: 'Europe/Dublin', sessions: Math.round(views * 0.04) },
        { place: 'unknown',       sessions: Math.round(views * 0.02) }
      ],
      hours: [0,1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23].map(function (h) {
        var shape = h < 7 ? 0.2 : h < 12 ? 0.7 : h < 17 ? 1 : h < 22 ? 1.5 : 0.6;
        return { hour: h, entries: Math.round((entries / 24) * shape) };
      }),
      prizes: (function () {
        var claimed = Math.round(entries * 0.07);
        var claimedDaily = days.slice(-7).map(function (d) {
          return { day: d.day, claimed: d.wins };
        });
        var PRIZES = [
          { flavour: 'Jackpot',             prize: 'Four Loko T-Shirt' },
          { flavour: 'Strawberry Lemonade', prize: 'Four Loko Cap' },
          { flavour: 'White',               prize: 'Four Loko Tumbler' },
          { flavour: 'Tropical',            prize: 'Four Loko Speaker' }
        ];
        var byPrize = PRIZES.map(function (pz, i) {
          /* uneven on purpose — real prizes never go at the same rate */
          var c = Math.min(100, Math.round(claimed / 4 * [1.18, 0.94, 1.05, 0.83][i]));
          return { flavour: pz.flavour, prize: pz.prize, total: 100,
                   claimed: c, remaining: 100 - c, sent: Math.round(c * 0.6) };
        });
        return { total: 400, claimed: claimed, remaining: 400 - claimed,
                 due: Math.min(400, claimed + 40),
                 firstAt: '2026-09-01T00:00:00+01:00',
                 lastAt: '2026-10-16T23:00:00+01:00',
                 byPrize: byPrize,
                 claimedDaily: claimedDaily };
      }())
    }});
  }

  function easeOutBackUnused() {}

  function waitFor(cond, cb) {
    if (cond()) return cb();
    setTimeout(function () { waitFor(cond, cb); }, 80);
  }
}());
